import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Table, Badge, Modal
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ReactDOM from 'react-dom';
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";

export default function NouvelleCommande() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [fournisseurs, setFournisseurs] = useState([]);
    const [produits, setProduits] = useState([]);
    const [produitsAjoutes, setProduitsAjoutes] = useState([]); // Pour le feedback
    
    // Recherche fournisseur
    const [searchFournisseur, setSearchFournisseur] = useState('');
    const [showFournisseurResults, setShowFournisseurResults] = useState(false);
    const fournisseurInputRef = useRef(null);
    const [fournisseurPosition, setFournisseurPosition] = useState({ top: 0, left: 0, width: 0 });
    
    // Recherche produit
    const [searchProduit, setSearchProduit] = useState('');
    const [showProduitResults, setShowProduitResults] = useState(false);
    const produitInputRef = useRef(null);
    const [produitPosition, setProduitPosition] = useState({ top: 0, left: 0, width: 0 });
    
    const [formData, setFormData] = useState({
        id_fournisseur: '',
        fournisseur_nom: '',
        date_commande: new Date().toISOString().split('T')[0],
        date_livraison_prevue: '',
        conditions_reglement: '',
        lignes: []
    });

    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [produitSelectionne, setProduitSelectionne] = useState(null);
    const [quantite, setQuantite] = useState(1);
    const [prix, setPrix] = useState(0);
    const [remise, setRemise] = useState(0);

    // ✅ Récupérer les paramètres d'URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const produitId = params.get('produit');
        const quantiteParam = params.get('quantite');
        
        console.log("Paramètres URL:", { produitId, quantiteParam });
        
        if (produitId) {
            // Stocker temporairement pour ajouter après chargement des produits
            sessionStorage.setItem('produit_auto', produitId);
            sessionStorage.setItem('quantite_auto', quantiteParam || '1');
        }
    }, [location.search]);

    // ✅ Ajouter automatiquement le produit quand les produits sont chargés
    useEffect(() => {
        const produitId = sessionStorage.getItem('produit_auto');
        if (produitId && produits.length > 0) {
            const produit = produits.find(p => p.id_produit === parseInt(produitId));
            const quantiteAuto = parseInt(sessionStorage.getItem('quantite_auto')) || 1;
            
            if (produit) {
                console.log("Ajout automatique du produit:", produit);
                
                const nouvelleLigne = {
                    id_produit: produit.id_produit,
                    reference: produit.reference,
                    designation: produit.designation,
                    quantite_commandee: quantiteAuto,
                    prix_unitaire_ht: produit.prix_achat_ht || 0,
                    remise: 0,
                    tva_taux: produit.tva_taux || 18.00,
                    css_taux: produit.css_taux || 1.00,
                    montant_ht: quantiteAuto * (produit.prix_achat_ht || 0)
                };
                
                setFormData(prev => ({
                    ...prev,
                    lignes: [...prev.lignes, nouvelleLigne]
                }));
                
                // Afficher un message de succès
                setProduitsAjoutes([produit]);
                setTimeout(() => setProduitsAjoutes([]), 3000);
                
                // Nettoyer sessionStorage
                sessionStorage.removeItem('produit_auto');
                sessionStorage.removeItem('quantite_auto');
            }
        }
    }, [produits]);

    // ✅ Fonction pour ajouter plusieurs produits depuis location.state (si existant)
    useEffect(() => {
        if (location.state?.produits) {
            handleAjouterProduits(location.state.produits);
        }
    }, [location.state, produits]);

    // Fonction pour ajouter plusieurs produits en une fois
    const handleAjouterProduits = (produitsData) => {
        if (!produitsData || produitsData.length === 0 || produits.length === 0) return;

        const nouvellesLignes = produitsData.map(p => {
            const produitComplet = produits.find(prod => prod.id_produit === p.id);
            
            if (!produitComplet) return null;
            
            return {
                id_produit: produitComplet.id_produit,
                reference: produitComplet.reference,
                designation: produitComplet.designation,
                quantite_commandee: p.quantite || 1,
                prix_unitaire_ht: produitComplet.prix_achat_ht || 0,
                remise: 0,
                tva_taux: produitComplet.tva_taux || 18.00,
                css_taux: produitComplet.css_taux || 1.00,
                montant_ht: (p.quantite || 1) * (produitComplet.prix_achat_ht || 0)
            };
        }).filter(l => l !== null);

        if (nouvellesLignes.length > 0) {
            setFormData(prev => ({
                ...prev,
                lignes: [...prev.lignes, ...nouvellesLignes]
            }));
            
            setProduitsAjoutes(nouvellesLignes);
            setTimeout(() => setProduitsAjoutes([]), 3000);
        }
    };

    // Mettre à jour la position du dropdown fournisseur
    const updateFournisseurPosition = () => {
        if (fournisseurInputRef.current) {
            const rect = fournisseurInputRef.current.getBoundingClientRect();
            setFournisseurPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // Mettre à jour la position du dropdown produit
    const updateProduitPosition = () => {
        if (produitInputRef.current) {
            const rect = produitInputRef.current.getBoundingClientRect();
            setProduitPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // Mettre à jour les positions au scroll
    useEffect(() => {
        const handleScroll = () => {
            if (showFournisseurResults) updateFournisseurPosition();
            if (showProduitResults) updateProduitPosition();
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showFournisseurResults, showProduitResults]);

    const fetchFournisseurs = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch('https://sawd.julesferry-ga.com/api/fournisseurs.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            console.log("Fournisseurs reçus:", result.data);
            setFournisseurs(result.data || []);
        } catch (err) {
            console.error("Erreur chargement fournisseurs", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch('https://sawd.julesferry-ga.com/api/produits.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    useEffect(() => {
        fetchFournisseurs();
        fetchProduits();
    }, []);

    const handleFournisseurSelect = (fournisseur) => {
        setFormData({
            ...formData,
            id_fournisseur: fournisseur.id_fournisseur,
            fournisseur_nom: fournisseur.raison_sociale,
            conditions_reglement: fournisseur.conditions_reglement || ''
        });
        setSearchFournisseur(fournisseur.raison_sociale);
        setShowFournisseurResults(false);
    };

    const handleAjouterProduit = (produit) => {
        setProduitSelectionne(produit);
        setPrix(produit.prix_achat_ht || 0);
        setQuantite(1);
        setRemise(0);
        setShowProduitModal(true);
        setShowProduitResults(false);
    };

    const handleConfirmerProduit = () => {
        const nouvelleLigne = {
            id_produit: produitSelectionne.id_produit,
            reference: produitSelectionne.reference,
            designation: produitSelectionne.designation,
            quantite_commandee: quantite,
            prix_unitaire_ht: prix,
            remise: remise,
            tva_taux: produitSelectionne.tva_taux || 18.00,
            css_taux: produitSelectionne.css_taux || 1.00,
            montant_ht: quantite * prix * (1 - remise/100)
        };

        setFormData({
            ...formData,
            lignes: [...formData.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
        setSearchProduit('');
    };

    const handleSupprimerLigne = (index) => {
        const nouvellesLignes = formData.lignes.filter((_, i) => i !== index);
        setFormData({ ...formData, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        const montantHT = formData.lignes.reduce((acc, l) => acc + (l.montant_ht || 0), 0);
        const tva = formData.lignes.reduce((acc, l) => acc + ((l.montant_ht || 0) * (l.tva_taux || 18) / 100), 0);
        const css = formData.lignes.reduce((acc, l) => acc + ((l.montant_ht || 0) * (l.css_taux || 1) / 100), 0);
        return {
            montant_ht: montantHT,
            montant_tva: tva,
            montant_css: css,
            montant_ttc: montantHT + tva + css
        };
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.id_fournisseur) newErrors.fournisseur = "Veuillez sélectionner un fournisseur";
        if (formData.lignes.length === 0) newErrors.lignes = "Ajoutez au moins un produit";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setSubmitError('');

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const totaux = calculerTotaux();

            const response = await fetch('https://sawd.julesferry-ga.com/api/commandes_fournisseurs.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    id_fournisseur: formData.id_fournisseur,
                    date_commande: formData.date_commande,
                    date_livraison_prevue: formData.date_livraison_prevue,
                    conditions_reglement: formData.conditions_reglement,
                    ...totaux,
                    lignes: formData.lignes,
                    id_utilisateur: user?.id_utilisateur || 1
                })
            });

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    navigate('/achats/commandes');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setLoading(false);
        }
    };

    const totaux = calculerTotaux();

    // Filtrer les fournisseurs
    const fournisseursFiltres = fournisseurs.filter(f => 
        f.raison_sociale?.toLowerCase().includes(searchFournisseur.toLowerCase()) ||
        f.code_fournisseur?.toLowerCase().includes(searchFournisseur.toLowerCase())
    );

    // Filtrer les produits
    const produitsFiltres = produits.filter(p => 
        p.designation?.toLowerCase().includes(searchProduit.toLowerCase()) ||
        p.reference?.toLowerCase().includes(searchProduit.toLowerCase())
    );

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h1 className="page-title mb-1">Nouvelle commande</h1>
                                    <p className="text-muted">Créer une commande fournisseur</p>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/achats/commandes')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {submitSuccess && (
                        <Alert variant="success">Commande créée avec succès !</Alert>
                    )}

                    {submitError && (
                        <Alert variant="danger">{submitError}</Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={8}>
                                {/* Sélection fournisseur */}
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-white">
                                        <h5 className="mb-0">Fournisseur</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form.Group>
                                            <Form.Label>Rechercher un fournisseur <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                ref={fournisseurInputRef}
                                                type="text"
                                                placeholder="Nom du fournisseur..."
                                                value={searchFournisseur}
                                                onChange={(e) => {
                                                    setSearchFournisseur(e.target.value);
                                                    setShowFournisseurResults(true);
                                                    setTimeout(updateFournisseurPosition, 10);
                                                }}
                                                onFocus={(e) => {
                                                    setShowFournisseurResults(true);
                                                    updateFournisseurPosition();
                                                }}
                                                onBlur={() => setTimeout(() => setShowFournisseurResults(false), 200)}
                                                isInvalid={!!errors.fournisseur}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.fournisseur}
                                            </Form.Control.Feedback>
                                            
                                            {/* Portail pour les résultats fournisseurs */}
                                            {showFournisseurResults && searchFournisseur && fournisseursFiltres.length > 0 && ReactDOM.createPortal(
                                                <div 
                                                    style={{
                                                        position: 'absolute',
                                                        top: fournisseurPosition.top,
                                                        left: fournisseurPosition.left,
                                                        width: fournisseurPosition.width,
                                                        zIndex: 99999,
                                                        backgroundColor: 'white',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto'
                                                    }}
                                                >
                                                    {fournisseursFiltres.map(f => (
                                                        <div
                                                            key={f.id_fournisseur}
                                                            className="p-3 border-bottom"
                                                            onClick={() => handleFournisseurSelect(f)}
                                                            style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                        >
                                                            <strong>{f.raison_sociale}</strong>
                                                            <div className="small text-muted">
                                                                {f.code_fournisseur} | {f.telephone || 'Tél non renseigné'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>,
                                                document.body
                                            )}

                                            {showFournisseurResults && searchFournisseur && fournisseursFiltres.length === 0 && ReactDOM.createPortal(
                                                <div 
                                                    style={{
                                                        position: 'absolute',
                                                        top: fournisseurPosition.top,
                                                        left: fournisseurPosition.left,
                                                        width: fournisseurPosition.width,
                                                        zIndex: 99999,
                                                        backgroundColor: 'white',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                                                        padding: '15px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <i className="ri-search-line fs-4 text-muted mb-2 d-block"></i>
                                                    <p className="text-muted mb-0">Aucun fournisseur trouvé</p>
                                                </div>,
                                                document.body
                                            )}
                                        </Form.Group>

                                       {formData.id_fournisseur && (
    <Card className="border-0 shadow-sm mt-3" style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f2ff 100%)',
        borderRadius: '12px',
        borderLeft: '4px solid #4361ee'
    }}>
        <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-circle">
                        <i className="ri-check-line text-primary fs-5"></i>
                    </div>
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-dark">{formData.fournisseur_nom}</span>
                            <Badge bg="primary" className="rounded-pill px-2" style={{ fontSize: '0.7rem' }}>
                                Sélectionné
                            </Badge>
                        </div>
                        <div className="d-flex align-items-center gap-3 mt-1">
                            <small className="text-muted">
                                <i className="ri-file-text-line me-1 text-primary"></i>
                                {formData.conditions_reglement || 'Conditions non spécifiées'}
                            </small>
                        </div>
                    </div>
                </div>
                <Button 
                    variant="link" 
                    className="text-muted p-0"
                    style={{ textDecoration: 'none' }}
                    onClick={() => {
                        setFormData({
                            ...formData,
                            id_fournisseur: '',
                            fournisseur_nom: '',
                            conditions_reglement: ''
                        });
                        setSearchFournisseur('');
                    }}
                >
                    <i className="ri-close-circle-line fs-5"></i>
                </Button>
            </div>
        </Card.Body>
    </Card>
)}
                                    </Card.Body>
                                </Card>

                                {/* Dates */}
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-white">
                                        <h5 className="mb-0">Dates</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Date commande</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={formData.date_commande}
                                                        onChange={(e) => setFormData({...formData, date_commande: e.target.value})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Date livraison prévue</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={formData.date_livraison_prevue}
                                                        onChange={(e) => setFormData({...formData, date_livraison_prevue: e.target.value})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Produits */}
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">Produits</h5>
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            onClick={() => setShowProduitResults(!showProduitResults)}
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter un produit
                                        </Button>
                                    </Card.Header>
                                    <Card.Body>
                                        {showProduitResults && (
                                            <div className="mb-4">
                                                <InputGroup>
                                                    <Form.Control
                                                        ref={produitInputRef}
                                                        type="text"
                                                        placeholder="Rechercher un produit..."
                                                        value={searchProduit}
                                                        onChange={(e) => {
                                                            setSearchProduit(e.target.value);
                                                            setTimeout(updateProduitPosition, 10);
                                                        }}
                                                        onFocus={updateProduitPosition}
                                                    />
                                                </InputGroup>
                                                
                                                {searchProduit && produitsFiltres.length > 0 && ReactDOM.createPortal(
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            top: produitPosition.top,
                                                            left: produitPosition.left,
                                                            width: produitPosition.width,
                                                            zIndex: 99999,
                                                            backgroundColor: 'white',
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: '8px',
                                                            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                                                            maxHeight: '300px',
                                                            overflowY: 'auto'
                                                        }}
                                                    >
                                                        {produitsFiltres.map(p => (
                                                            <div
                                                                key={p.id_produit}
                                                                className="p-3 border-bottom d-flex justify-content-between align-items-center"
                                                                onClick={() => handleAjouterProduit(p)}
                                                                style={{ cursor: 'pointer' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                            >
                                                                <div>
                                                                    <strong>{p.designation}</strong>
                                                                    <div className="small text-muted">
                                                                        Réf: {p.reference} | Prix: {formatCurrency(p.prix_achat_ht)}
                                                                    </div>
                                                                </div>
                                                                <Badge bg="primary">Ajouter</Badge>
                                                            </div>
                                                        ))}
                                                    </div>,
                                                    document.body
                                                )}

                                                {searchProduit && produitsFiltres.length === 0 && ReactDOM.createPortal(
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            top: produitPosition.top,
                                                            left: produitPosition.left,
                                                            width: produitPosition.width,
                                                            zIndex: 99999,
                                                            backgroundColor: 'white',
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: '8px',
                                                            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                                                            padding: '15px',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        <i className="ri-search-line fs-4 text-muted mb-2 d-block"></i>
                                                        <p className="text-muted mb-0">Aucun produit trouvé</p>
                                                    </div>,
                                                    document.body
                                                )}
                                            </div>
                                        )}

                                        {formData.lignes.length > 0 ? (
                                            <Table responsive>
                                                <thead>
                                                    <tr>
                                                        <th>Produit</th>
                                                        <th>Réf.</th>
                                                        <th className="text-end">Qté</th>
                                                        <th className="text-end">Prix unit.</th>
                                                        <th className="text-end">Remise</th>
                                                        <th className="text-end">Total HT</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.lignes.map((ligne, index) => (
                                                        <tr key={index}>
                                                            <td>{ligne.designation}</td>
                                                            <td><small>{ligne.reference}</small></td>
                                                            <td className="text-end">{ligne.quantite_commandee}</td>
                                                            <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                            <td className="text-end">{ligne.remise}%</td>
                                                            <td className="text-end fw-bold">{formatCurrency(ligne.montant_ht)}</td>
                                                            <td className="text-center">
                                                                <Button 
                                                                    variant="link" 
                                                                    className="text-danger p-0"
                                                                    onClick={() => handleSupprimerLigne(index)}
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <div className="text-center py-4">
                                                <i className="ri-shopping-bag-line fs-1 text-muted mb-3"></i>
                                                <p className="text-muted">Aucun produit ajouté</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={4}>
                                {/* Résumé */}
                                <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px',zIndex:'1' }}>
                                    <Card.Header className="bg-white">
                                        <h5 className="mb-0">Résumé</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <small className="text-muted">Total HT</small>
                                            <h4 className="text-end">{formatCurrency(totaux.montant_ht)}</h4>
                                        </div>
                                        <div className="mb-3">
                                            <small className="text-muted">TVA (18%)</small>
                                            <h4 className="text-end">{formatCurrency(totaux.montant_tva)}</h4>
                                        </div>
                                        <div className="mb-3">
                                            <small className="text-muted">CSS (1%)</small>
                                            <h4 className="text-end">{formatCurrency(totaux.montant_css)}</h4>
                                        </div>
                                        <hr />
                                        <div className="mb-4">
                                            <small className="text-muted">Total TTC</small>
                                            <h2 className="text-end text-primary">{formatCurrency(totaux.montant_ttc)}</h2>
                                        </div>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Conditions de règlement</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={formData.conditions_reglement}
                                                onChange={(e) => setFormData({...formData, conditions_reglement: e.target.value})}
                                                placeholder="Ex: Paiement 30 jours..."
                                            />
                                        </Form.Group>

                                        <Button 
                                            type="submit" 
                                            variant="primary" 
                                            className="w-100 py-3"
                                            disabled={loading}
                                            style={{
                                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                border: 'none'
                                            }}
                                        >
                                            {loading ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                <>
                                                    <i className="ri-check-line me-2"></i>
                                                    Créer la commande
                                                </>
                                            )}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>

                    {/* Modal d'ajout de produit */}
                    <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Ajouter {produitSelectionne?.designation}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Quantité</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={quantite}
                                        onChange={(e) => setQuantite(parseInt(e.target.value) || 1)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prix unitaire HT</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={prix}
                                            onChange={(e) => setPrix(parseFloat(e.target.value) || 0)}
                                        />
                                        <InputGroup.Text>FCFA</InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Remise (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={remise}
                                        onChange={(e) => setRemise(parseFloat(e.target.value) || 0)}
                                    />
                                </Form.Group>
                                <hr />
                                <div className="d-flex justify-content-between">
                                    <span>Total HT</span>
                                    <strong>{formatCurrency(quantite * prix * (1 - remise/100))}</strong>
                                </div>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowProduitModal(false)}>
                                Annuler
                            </Button>
                            <Button variant="primary" onClick={handleConfirmerProduit}>
                                Ajouter
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
                <Footer />
            </div>
        </>
    );
}